import { CLASS_NAME_DEFAULT } from "../../../constants"
import Skeleton from "../base"

interface Props {
  className?: string
}

const SubscriptionDetailsSkeleton = ({
  className = CLASS_NAME_DEFAULT
}: Props) => (
  <div className={`h-full flex flex-col ${className}`}>
    <div className="pt-6 bg-white">
      <Skeleton>
        {/* Header section with breadcrumbs and user info */}
        <div className="px-6">
          {/* Breadcrumbs */}
          <div className="flex items-center">
            <div className="h-4 bg-gray-200 rounded w-20"></div>
            <div className="mx-2 h-4 bg-gray-100 rounded w-0.5"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>

          {/* User info section */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex items-center">
              {/* Avatar */}
              <div className="w-16 h-16 bg-gray-200 rounded-full mr-4"></div>
              <div>
                <div className="h-6 bg-gray-200 rounded w-40"></div>
                <div className="h-4 bg-gray-100 rounded w-48 mt-2"></div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <div className="h-10 bg-gray-200 rounded w-16"></div>
              <div className="h-10 bg-gray-200 rounded w-20"></div>
            </div>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex border-b border-gray-200 mt-10 px-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="mr-8 pb-4">
              <div className="h-5 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>

        {/* Main content sections */}
        <div className="space-y-6 px-6 mt-10">
          {/* Personal Details Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-16"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-100 rounded w-32"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-16"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-100 rounded w-40"></div>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-16"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-100 rounded w-32"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                  <div className="h-4 bg-gray-100 rounded w-20"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-32"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Property Location Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-5 bg-gray-200 rounded w-32"></div>
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-100 rounded w-20"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                  <div className="h-4 bg-gray-100 rounded w-16"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                  <div className="h-4 bg-gray-100 rounded w-8"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-100 rounded w-12"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                  <div className="h-4 bg-gray-100 rounded w-8"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-100 rounded w-12"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Loan Scenario Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-5 bg-gray-200 rounded w-24"></div>
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-16"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-100 rounded w-20"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-100 rounded w-24"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-24"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-100 rounded w-20"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-12"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-24"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-20"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-100 rounded w-16"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Skeleton>
    </div>
  </div>
)

export default SubscriptionDetailsSkeleton
